<template>
  <div style="margin:20px">
    <!-- one flex column with a single gap rather than per-row margins: the page
         rail's own bottom margin differs by theme (14px horizon / 25px classic),
         so relying on collapsed margins made the header gap theme-dependent.
         The rail's margin is zeroed below so all three gaps are exactly equal. -->
    <div class="ontime-top-rows">
      <FiPageRail title="Driver On-Time % Report">
        <template slot="actions">
          <actuals-on-time-report-help :large-fleet-vehicles="large_fleet_vehicles" />
        </template>
      </FiPageRail>

      <div style="font-size: 22px; font-weight: bold; position: relative; margin-left: 20px; margin-right: 20px; gap: 22px;" class="d-flex align-items-center justify-content-center flex-wrap ontime-controls-row">
        <span class="d-flex align-items-center" style="gap: 8px;">
          <b-button size="sm" variant="outline-secondary" title="Previous day" v-on:click.prevent="shift_dates(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
          <date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"></date-picker>
          <span>to</span>
          <date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"></date-picker>
          <b-button size="sm" variant="outline-secondary" title="Next day" v-on:click.prevent="shift_dates(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
        </span>
        <span v-if="base_options && base_options.length > 2" class="d-flex align-items-center" style="gap: 8px;">Base<b-form-select v-model="selected_base" :options="base_options" style="font-size: 16px; font-weight: bold; width:220px"/></span>
        <span v-if="type_options.length > 1" class="d-flex align-items-center" style="gap: 8px;">Type<b-form-select v-model="selected_type" :options="type_options" style="font-size: 16px; font-weight: bold; width:180px"/></span>
        <b-button v-on:click.prevent="load_data()" :disabled="loading || runDisabled">Get Results</b-button>
        <b-button size="md" variant="success" :disabled="loading || runDisabled || !on_time_by_driver.length" v-on:click.prevent="download">Download</b-button>
      </div>

      <div style="font-size: 15px; font-weight: bold; margin-left: 20px; margin-right: 20px; gap: 24px;" class="d-flex align-items-center justify-content-center ontime-threshold-row">
        <span>To Yard within&nbsp;<b-form-select v-model="to_yard_minutes" :options="threshold_options" style="width:75px; display:inline-block;"/>&nbsp;m</span>
        <span>Depart Yard within&nbsp;<b-form-select v-model="depart_yard_minutes" :options="threshold_options" style="width:75px; display:inline-block;"/>&nbsp;m</span>
        <span>To Pickup within&nbsp;<b-form-select v-model="to_pickup_minutes" :options="threshold_options" style="width:75px; display:inline-block;"/>&nbsp;m</span>
      </div>
    </div>

    <div v-if="loading">
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="runDisabled" class="pva-empty">
      Select a base, then click <strong>Get Results</strong> to load results.
    </div>

    <div v-else-if="summary.range_too_large" class="pva-empty">
      That date range covers {{ summary.row_count | numberWithCommas }} trips, more than the
      {{ summary.max_rows | numberWithCommas }} this report can total up in one go.
      Please pick a shorter range &mdash; or a single base, if you have more than one.
    </div>

    <div v-else-if="summary.totals">
      <div v-if="summary.truncated" class="pva-empty">
        Showing partial results &mdash; this range returned more trips than the report can
        load at once, so the percentages below are based on some of the range, not all of it.
      </div>
      <div v-if="summary.totals.with_hos_times === 0" class="pva-empty">
        No successful actuals with both HOS start and finish times found between {{ summary.start_date }} and {{ summary.end_date }}.
      </div>

      <template v-else>
        <div v-if="on_time_metric_tiles.length" class="pva-section">
          <div class="pva-ontime-grid">
            <div
              v-for="tile in on_time_metric_tiles"
              :key="'ontime-' + tile.key"
              class="pva-ontime-tile"
            >
              <div class="pva-ontime-label">{{ tile.label }}</div>
              <div class="pva-ontime-value" :class="on_time_pct_class(tile.on_time_pct)">{{ tile.on_time_pct }}%</div>
              <div class="pva-ontime-meta">{{ tile.on_time_count }} of {{ tile.count }}</div>
              <div class="pva-ontime-rule">{{ tile.rule }}</div>
            </div>
          </div>
        </div>

        <div class="pva-section">
          <div class="pva-section-header">By Driver</div>
          <div v-if="on_time_by_driver.length === 0" class="pva-accuracy-empty">
            Not enough successful bookings per driver (need at least 2) to show a breakdown.
          </div>
          <table v-else class="pva-table">
            <tr>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('driver_name')">Driver <i class="fa" :class="on_time_driver_sort_icon('driver_name')"></i></th>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('movements')">Bookings <i class="fa" :class="on_time_driver_sort_icon('movements')"></i></th>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('to_yard_pct')">To Yard <i class="fa" :class="on_time_driver_sort_icon('to_yard_pct')"></i></th>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('depart_yard_pct')">Depart Yard <i class="fa" :class="on_time_driver_sort_icon('depart_yard_pct')"></i></th>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('to_pickup_pct')">To Pickup <i class="fa" :class="on_time_driver_sort_icon('to_pickup_pct')"></i></th>
            </tr>
            <tr v-for="entry in paginated_on_time_by_driver" :key="'ontime-driver-' + entry.driver_name">
              <td><a href="#" v-on:click.prevent="show_driver_detail(entry)">{{ entry.driver_name }}</a></td>
              <td>{{ entry.movements }}</td>
              <td :class="on_time_pct_class(entry.to_yard_pct)">{{ entry.to_yard_pct }}% <span class="pva-more-note">({{ entry.to_yard_on_time_count }}/{{ entry.to_yard_count }})</span></td>
              <td :class="on_time_pct_class(entry.depart_yard_pct)">{{ entry.depart_yard_pct }}% <span class="pva-more-note">({{ entry.depart_yard_on_time_count }}/{{ entry.depart_yard_count }})</span></td>
              <td :class="on_time_pct_class(entry.to_pickup_pct)">{{ entry.to_pickup_pct }}% <span class="pva-more-note">({{ entry.to_pickup_on_time_count }}/{{ entry.to_pickup_count }})</span></td>
            </tr>
          </table>
          <b-pagination
            v-if="sorted_on_time_by_driver.length > driver_per_page"
            v-model="driver_page"
            :total-rows="sorted_on_time_by_driver.length"
            :per-page="driver_per_page"
            align="center"
          ></b-pagination>
        </div>
      </template>
    </div>

    <div v-else-if="initial_load_done || large_fleet" class="pva-empty">
      Click <strong>Get Results</strong> to load results.
    </div>

    <b-modal
      v-model="show_driver_modal"
      :title="selected_driver_name + ' — Actuals'"
      size="xl"
      dialog-class="ontime-driver-modal-dialog-wide-tbn"
      ok-only
      scrollable
    >
      <div v-if="!selected_driver_movements.length" class="pva-accuracy-empty">
        No movement detail available.
      </div>
      <div v-else class="table-responsive">
        <table class="pva-table pva-driver-detail-table">
          <tr>
            <th>Booking</th>
            <th>Vehicle</th>
            <th>Yard Time</th>
            <th>HOS Start</th>
            <th>To Yard &Delta;</th>
            <th>Planned Depart</th>
            <th>Vehicle Depart</th>
            <th>Depart Yard &Delta;</th>
            <th>Planned Pickup</th>
            <th>Actual Pickup</th>
            <th>To Pickup &Delta;</th>
          </tr>
          <tr v-for="row in selected_driver_movements" :key="'driver-detail-' + row.booking_hash">
            <td><router-link :to="booking_link(row)">{{ booking_label(row) }}</router-link></td>
            <td>{{ row.vehicle }}</td>
            <td>{{ row.planned_start | formatDateTimeSmall }}</td>
            <td>{{ row.hos_start | formatDateTimeSmall }}</td>
            <td :class="gate_class(row.to_yard_on_time)">{{ format_signed_minutes(row.start_delta_minutes) }}</td>
            <td>{{ row.planned_yard_departure | formatDateTimeSmall }}</td>
            <td>{{ row.vehicle_start | formatDateTimeSmall }}</td>
            <td :class="gate_class(row.depart_yard_on_time)">{{ format_signed_minutes(row.vehicle_start_delta_minutes) }}</td>
            <td>{{ row.planned_pickup | formatDateTimeSmall }}</td>
            <td>{{ row.actual_pickup | formatDateTimeSmall }}</td>
            <td :class="gate_class(row.to_pickup_on_time)">{{ format_signed_minutes(row.to_pickup_delta_minutes) }}</td>
          </tr>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {justDate} from '../helpers/dates'
import moment from 'moment'
import axios from 'axios'
import {api_url, get_tbn_actuals_on_time_report, get_booking_bases} from '../networking/tbn_apis'
import actuals_on_time_report_help from '@/components/actuals_on_time_report_help'

// the standard TBN booking types, so the Type picker is usable before the first
// search instead of only after a response reveals which types are present.
// "booking" is the value TBN actually stores for charters on most tenants; the
// backend treats booking/charter as the same type, so either matches.
const KNOWN_BOOKING_TYPES = ['booking', 'scheduledservice', 'schoolcontract']

// above this many vehicles the range query is slow enough that running it
// unprompted on page load isn't worth it -- matches fleet_service.py's
// VIP_FLEET_THRESHOLD, the existing "large fleet" line in this codebase.
const LARGE_FLEET_VEHICLES = 100

export default {
  name: 'actuals_on_time_report_tbn',

  components: {
    'actuals-on-time-report-help': actuals_on_time_report_help,
  },

  data() {
    return {
      loading: false,
      summary: {},
      on_time_metrics: {},
      on_time_by_driver: [],
      on_time_driver_sort_col: 'driver_name',
      on_time_driver_sort_dir: 'asc',
      driver_page: 1,
      driver_per_page: 20,
      show_driver_modal: false,
      selected_driver_name: '',
      selected_driver_movements: [],
      initial_load_done: false,
      base_options: [],
      base_required: false,
      selected_base: '',
      // every booking type present in the range, from the last response's
      // summary.booking_types (computed pre-filter server-side, so the list
      // stays put while one type is selected)
      booking_types: [],
      selected_type: '',
      vehicle_count: null,
      details_fromDate: justDate(moment().subtract(6, 'days')),
      details_toDate: justDate(moment()),
      to_yard_minutes: 0,
      depart_yard_minutes: 0,
      to_pickup_minutes: 0,
      threshold_options: [0, 5, 10, 15, 20, 30, 45, 60],
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        // the report reads actuals, which are kept indefinitely -- the old
        // 180-day floor was never backed by anything. Capped at ~13 months so
        // a single request still comfortably finishes inside the gateway
        // timeout; a year covers annual and semi-annual driver reviews.
        minDate: moment().subtract(400, 'days'),
        maxDate: moment().add(1, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    on_time_metric_tiles() {
      const defs = [
        {
          key: 'to_yard',
          label: 'On Time To Yard',
          rule: this.to_yard_minutes > 0
            ? `HOS on duty & assigned to vehicle within ${this.to_yard_minutes}m after planned start`
            : 'HOS on duty & assigned to vehicle at or before planned start',
        },
        {
          key: 'depart_yard',
          label: 'On Time To Depart Yard',
          rule: this.depart_yard_minutes > 0
            ? `Vehicle departure (first departure) within ${this.depart_yard_minutes}m after planned start`
            : 'Vehicle departure (first departure) at or before planned start',
        },
        {
          key: 'to_pickup',
          label: 'On Time To Pickup',
          rule: this.to_pickup_minutes > 0
            ? `Arrived at the first pickup within ${this.to_pickup_minutes}m after its planned arrival time`
            : 'Arrived at the first pickup at or before its planned arrival time',
        },
      ]
      return defs
        .map(def => ({ ...def, ...(this.on_time_metrics[def.key] || {}) }))
        .filter(tile => tile.count > 0)
    },
    sorted_on_time_by_driver() {
      const col = this.on_time_driver_sort_col
      const dir = this.on_time_driver_sort_dir === 'asc' ? 1 : -1
      const pick = entry => (col === 'driver_name' ? (entry.driver_name || '').toLowerCase() : Number(entry[col] || 0))
      return [...this.on_time_by_driver].sort((a, b) => {
        const av = pick(a), bv = pick(b)
        if (av < bv)
          return -dir
        if (av > bv)
          return dir
        return 0
      })
    },
    paginated_on_time_by_driver() {
      const start = (this.driver_page - 1) * this.driver_per_page
      return this.sorted_on_time_by_driver.slice(start, start + this.driver_per_page)
    },
    runDisabled() {
      return this.base_required && !this.selected_base
    },
    large_fleet() {
      return this.vehicle_count != null && this.vehicle_count > LARGE_FLEET_VEHICLES
    },
    // handed to the help modal so its text quotes the threshold actually in use
    large_fleet_vehicles() {
      return LARGE_FLEET_VEHICLES
    },
    // starts from the standard types so the picker is there before the first
    // search, then folds in anything else the loaded range actually contains.
    // Never shrinks, so options don't vanish under the user mid-session.
    type_options() {
      const types = [...KNOWN_BOOKING_TYPES]
      this.booking_types.forEach(type => {
        if (!types.includes(type))
          types.push(type)
      })
      if (this.selected_type && !types.includes(this.selected_type))
        types.push(this.selected_type)
      return [
        { value: '', text: 'All Types' },
        ...types.map(type => ({ value: type, text: this.type_label(type) })),
      ]
    },
  },

  async created() {
    if (this.$route.query.fromDate)
      this.details_fromDate = this.$route.query.fromDate
    if (this.$route.query.toDate)
      this.details_toDate = this.$route.query.toDate
    await this.load_bases()
    await this.try_initial_load()
  },

  watch: {
    user: {
      deep: true,
      async handler() {
        this.initial_load_done = false
        this.selected_base = ''
        this.selected_type = ''
        this.booking_types = []
        await this.load_bases()
        await this.try_initial_load()
      },
    },
    // booking_types and selected_type deliberately survive a base change: the
    // picker shouldn't vanish (and the chosen type shouldn't silently reset)
    // just because the base moved. The next load refreshes the type list from
    // that base's own rows.
    selected_base() {
      this.clear_results()
    },
    // the type filter is applied server-side, so results are cleared rather
    // than left showing figures that no longer match the selection -- nothing
    // is re-queried until Get Results is clicked. booking_types is kept so the
    // picker itself stays put.
    selected_type() {
      this.clear_results()
    },
  },

  methods: {
    async load_bases() {
      if (!this.user || !this.user.tenant)
        return
      try {
        const { value: { data = {} } } = await get_booking_bases(this.user.tenant)
        const options = (data.summary && data.summary.base_options) || []
        const yardCount = Math.max(0, options.length - 1)
        this.base_required = yardCount > 15
        const count = data.summary && data.summary.vehicle_count
        this.vehicle_count = count == null ? null : Number(count)
        if (this.base_required && options.length > 0) {
          options[0] = { ...options[0], value: '', text: 'Select Base' }
        }
        this.base_options = options
      } catch (e) {
        this.$emit('showAlert', 'error retrieving bases, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
    clear_results() {
      this.summary = {}
      this.on_time_metrics = {}
      this.on_time_by_driver = []
      this.show_driver_modal = false
    },
    // large fleets wait for an explicit Get Results -- the range query is slow
    // enough there that spending it on a page the user may just be passing
    // through isn't worth it.
    async try_initial_load() {
      if (this.initial_load_done || !this.user || !this.user.tenant || this.runDisabled || this.large_fleet)
        return
      this.initial_load_done = true
      await this.load_data()
    },
    async load_data() {
      if (!this.user || !this.user.tenant)
        return
      if (this.runDisabled) {
        this.$emit('showAlert', 'please select a base before running the report', 'warning', 5000)
        return
      }
      this.loading = true
      try {
        const from = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const to = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const { value: { data = {} } } = await get_tbn_actuals_on_time_report(
          this.user.tenant, from, to, undefined, this.selected_base,
          this.to_yard_minutes, this.depart_yard_minutes, this.to_pickup_minutes,
          this.selected_type,
        )
        if (this.base_options.length === 0 && data.summary && data.summary.bases) {
          this.base_options = data.summary.bases
          if (this.base_options.length > 0 && this.base_options[0].text === 'Select Base') {
            this.base_required = true
          }
        }
        this.summary = data.summary || {}
        this.booking_types = this.summary.booking_types || []
        this.on_time_metrics = data.on_time_metrics || {}
        this.on_time_by_driver = data.on_time_by_driver || []
        this.driver_page = 1
        this.show_driver_modal = false
        if (this.summary.timezone)
          moment.tz.setDefault(this.summary.timezone)
      } catch (e) {
        this.$emit('showAlert', 'error loading data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    shift_dates(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
    },
    async download() {
      this.$emit('showAlert', 'processing download request', 'info', 5000)
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}tbn/reports/actuals/on_time/download`,
          {
            tenant: this.user.tenant,
            start_date: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            end_date: moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            base: this.selected_base,
            type: this.selected_type,
            to_yard_minutes: this.to_yard_minutes,
            depart_yard_minutes: this.depart_yard_minutes,
            to_pickup_minutes: this.to_pickup_minutes,
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
            'download',
            `actuals-on-time-tbn-${this.details_fromDate}-${this.details_toDate}.csv`
        )
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(() => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'data could not be downloaded.', 'danger')
      })
    },
    // same mapping the multiday OTP report uses for its type chips
    type_label(type) {
      const labels = {
        'booking': 'Charter',
        'charter': 'Charter',
        'scheduledservice': 'Scheduled Service',
        'schoolcontract': 'School Contract',
      }
      return labels[type] || type
    },
    booking_label(booking) {
      return booking.booking_id || booking.booking_hash
    },
    booking_link(booking) {
      return { path: '/actuals_tbn', query: { booking_id: this.booking_label(booking) } }
    },
    format_minutes(value) {
      if (value == null)
        return '—'
      return `${Math.round(value)}m`
    },
    format_signed_minutes(value) {
      if (value == null)
        return '—'
      const rounded = Math.round(value)
      return `${rounded > 0 ? '+' : ''}${rounded}m`
    },
    on_time_pct_class(pct) {
      if (pct == null)
        return ''
      if (pct >= 90)
        return 'pva-on-time-good'
      if (pct < 70)
        return 'pva-on-time-bad'
      return ''
    },
    set_on_time_driver_sort(col) {
      if (this.on_time_driver_sort_col === col) {
        this.on_time_driver_sort_dir = this.on_time_driver_sort_dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.on_time_driver_sort_col = col
        this.on_time_driver_sort_dir = col === 'driver_name' ? 'asc' : 'desc'
      }
      this.driver_page = 1
    },
    on_time_driver_sort_icon(col) {
      if (this.on_time_driver_sort_col !== col)
        return 'fa-sort pva-th-sort-idle'
      return this.on_time_driver_sort_dir === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc'
    },
    show_driver_detail(entry) {
      this.selected_driver_name = entry.driver_name
      this.selected_driver_movements = entry.movements_detail || []
      this.show_driver_modal = true
    },
    gate_class(passed) {
      if (passed == null)
        return ''
      return passed ? 'pva-on-time-good' : 'pva-on-time-bad'
    },
  },
}
</script>

<style scoped>
/* spacing for the rail / controls / thresholds rows. A flex column with explicit
   per-row margin-top rather than block margins, because flex doesn't collapse
   margins -- each gap is exactly what's written here, independent of the rail's
   own theme-dependent bottom margin (zeroed in the unscoped block below). */
.ontime-top-rows {
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
}
/* half the gap used below it -- the controls sit tight under the page header */
.ontime-controls-row {
  margin-top: 13px;
}
.ontime-threshold-row {
  margin-top: 25px;
}
.pva-empty {
  font-size: 20px;
  margin: 40px;
  text-align: center;
}
.pva-section {
  margin-bottom: 30px;
  text-align: left;
}
.pva-section-header {
  font-size: 20px;
  font-weight: bold;
  border-bottom: 2px solid #888;
  margin-bottom: 10px;
  padding-bottom: 4px;
  text-align: left;
}
.pva-more-note {
  font-style: italic;
  font-size: 13px;
  color: #555;
}
.pva-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-bottom: 12px;
}
.pva-table th, .pva-table td {
  border: 1px solid #ddd;
  padding: 4px 8px;
  text-align: left;
}
.pva-table th { background-color: #f5f5f5; }
.pva-th-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.pva-th-sortable:hover { color: #000; }
.pva-th-sortable .fa {
  margin-left: 4px;
  font-size: 11px;
}
.pva-th-sort-idle { color: #bbb; }
.pva-on-time-good { color: #155724; font-weight: bold; }
.pva-on-time-bad { color: #721c24; font-weight: bold; }
.pva-accuracy-empty {
  font-style: italic;
  color: #666;
  font-size: 14px;
}
.pva-ontime-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.pva-ontime-tile {
  border: 1px solid #dee2e6;
  border-left: 4px solid #6c757d;
  border-radius: 8px;
  padding: 12px 14px;
  background: #f8f9fa;
}
.pva-ontime-label {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 6px;
}
.pva-ontime-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
}
.pva-ontime-meta {
  font-size: 12px;
  color: #555;
  margin-top: 4px;
}
.pva-ontime-rule {
  font-size: 12px;
  font-style: italic;
  color: #666;
  margin-top: 6px;
}
.pva-driver-detail-table td,
.pva-driver-detail-table th {
  white-space: nowrap;
}
@media (max-width: 900px) {
  .pva-ontime-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>

<style>
.ontime-driver-modal-dialog-wide-tbn {
  max-width: 1320px !important;
}

/* the header rail is a child component, and its root carries a theme-dependent
   bottom margin (14px horizon / 25px classic). Zeroing it here lets
   .ontime-top-rows' single gap set all three gaps identically. Scoped to the
   wrapper so it can't affect the rail on any other page. */
.ontime-top-rows > .fi-pr-rail,
.ontime-top-rows > .fi-pr-legacy {
  margin-bottom: 0;
}
</style>
